import { AnyAction } from 'redux';
import { PaginatedResponse } from '@synergy/api';

import {
    DELETE_SALESORDER,
    DUPLICATE_SALESORDER,
    RECEIVE_SALESORDER,
    RECEIVE_SALESORDERS,
    REQUEST_SALESORDER,
    REQUEST_SALESORDER_END,
    REQUEST_SALESORDER_START,
    RESET_SALESORDERS_STATE,
    RESET_SALESORDER_STATE,
} from './sales-order-actions';
import { SalesOrder } from './models/salesOrder';

export interface MappedSalesOrders {
    [id: number]: SalesOrder;
}

export interface SalesOrdersReducerState {
    entity?: SalesOrder;
    byId: MappedSalesOrders;
    flat: PaginatedResponse<SalesOrder>;
    requesting: boolean;
}

const initialSalesOrdersReducerState: SalesOrdersReducerState = {
    entity: undefined,
    byId: {},
    flat: { limit: 10, page: 0, total: 0, items: [] },
    requesting: false,
};

export const mapSalesOrdersById = (salesOrders: SalesOrder[]): MappedSalesOrders => {
    // eslint-disable-next-line @typescript-eslint/ban-types
    return salesOrders.reduce((accumulator: {}, salesOrder) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accumulator as any)[salesOrder.id] = salesOrder;
        return accumulator;
    }, {});
};

export const salesOrdersReducer = (
    state: SalesOrdersReducerState = initialSalesOrdersReducerState,
    action: AnyAction
): SalesOrdersReducerState => {
    const { type, ...rest } = action;
    switch (type) {
        case RECEIVE_SALESORDERS: {
            return {
                ...state,
                byId: mapSalesOrdersById(action['salesOrders'].items),
                flat: action['salesOrders'],
            };
        }

        case RECEIVE_SALESORDER: {
            // Add or update current sales-order array
            const paginatedSalesOrders = { ...state.flat };
            const salesOrders = [...paginatedSalesOrders.items];
            const existingSalesOrder = salesOrders.find(salesOrder => salesOrder.id === action['entity'].id);
            if (existingSalesOrder) {
                const index = salesOrders.indexOf(existingSalesOrder);
                salesOrders[index] = action['entity'];
            } else {
                salesOrders.unshift(action['entity']);
                // remove last one if surpassing page size
                if (salesOrders.length > paginatedSalesOrders.limit) {
                    salesOrders.pop();
                }
            }
            paginatedSalesOrders.items = salesOrders;

            const entity = action['entity'] ?? state.entity;

            return {
                ...state,
                flat: paginatedSalesOrders,
                byId: mapSalesOrdersById(salesOrders),
                entity,
                requesting: false,
            };
        }

        case RESET_SALESORDERS_STATE: {
            return {
                ...state,
                byId: initialSalesOrdersReducerState.byId,
                flat: initialSalesOrdersReducerState.flat,
            };
        }

        case RESET_SALESORDER_STATE: {
            return {
                ...state,
                entity: initialSalesOrdersReducerState.entity,
            };
        }

        case DELETE_SALESORDER: {
            const paginatedSalesOrders = { ...state.flat };
            const salesOrders = paginatedSalesOrders.items.filter(salesOrder => salesOrder.id !== rest['salesOrderId']);
            paginatedSalesOrders.items = salesOrders;
            paginatedSalesOrders.total--;

            return {
                ...state,
                flat: paginatedSalesOrders,
                byId: mapSalesOrdersById(salesOrders),
                entity: initialSalesOrdersReducerState.entity,
            };
        }

        case REQUEST_SALESORDER:
        case DUPLICATE_SALESORDER:
        case REQUEST_SALESORDER_START: {
            return {
                ...state,
                requesting: true,
            };
        }

        case REQUEST_SALESORDER_END: {
            return {
                ...state,
                requesting: false,
            };
        }

        default: {
            return state;
        }
    }
};
